import {
    GameUIMessageType,
    BetOptionItem,
    InfoLineUpdateFreespinCountMessage,
    FeatureStatusChangedMessage,
} from 'game-ui-types'
import { useUltraPlayStore } from '@stores/ultraPlayStore'
import { usePlayerInfoStore, useConfigurationStore, useSpinButtonSectionStore } from '@stores'

// Engine-sync logic for Ultra Play, extracted from App.tsx's window 'message' handler so it can be
// unit-tested in isolation. It owns the transient bet-recording / feature-grouping state (previously
// a set of useRefs in App) and drives the Ultra Play store in response to engine events:
//
//   - Shift-model bet recording: a bet starts on its autoplay count, but its balance only lands by
//     the NEXT count — so each bet is recorded one count late (and the last one on stop), reading
//     the balance after it resolved. This avoids a phantom first row and the off-by-one balance.
//   - Feature lifecycle (free spins): group the feature's spins under the triggering row, derive
//     per-spin win from the balance delta, and finish the feature on the first base count after it.
//   - Insufficient balance and batch re-trigger handling at autoplay stop.
//
// All store reads/writes go through the real stores (as in App); only outgoing engine messages are
// injected via `send`, so tests can drive real store state and assert on it while spying on `send`.

export type SendMessage = (message: unknown) => void

const defaultSend: SendMessage = (message) => window.postMessage(message, '*')

export const createUltraPlaySpinRecorder = (sendArg?: SendMessage) => {
    const send: SendMessage = sendArg ?? defaultSend

    // Spin that triggered the active feature — promoted to the "+N" group marker.
    let featureParentSpin: number | null = null
    // Latest free spins count from INFO_LINE_UPDATE_FREESPIN_COUNT — used for the "+N" badge.
    let featureCount = 0
    // Balance at the previous recorded feature spin — per-spin win is derived as the delta (this game
    // does not emit per-spin win during free spins).
    let featureLastBalance = 0
    // Highest free spin index recorded so far — updateFreespinCount fires multiple times per index.
    let lastFreespinIndex = 0
    // Set after a feature completes — skips the resumeAutoplay() count-update message (a non-spin).
    let skipNextSpinCount = false
    // Last engine autoplay count (UPDATE_AUTOPLAY_COUNT payload). 0 means the engine's batch finished
    // naturally; >0 at AUTOPLAY_STOPPED means it paused mid-batch for a feature.
    let lastEnginePayload = -1
    // pendingBet marks a started-but-unrecorded bet; betStartBalance is the balance at its start.
    let pendingBet = false
    let betStartBalance = 0

    const getBet = (): number => {
        const { selectedBetIndex } = useUltraPlayStore.getState()
        const { betOptions } = useConfigurationStore.getState()
        const betOption = betOptions[selectedBetIndex]

        return betOption === undefined
            ? 0
            : typeof betOption === 'number'
              ? betOption
              : (betOption as BetOptionItem).betAmount
    }

    // Records the bet that started on the previous count, now that its result is in `balance`
    // (win = balance - balanceAtBetStart + bet). Bets are recorded one count late by design, because
    // the balance only updates after the count that starts the next bet.
    const finalizePendingBet = (balance: number): boolean => {
        if (!pendingBet) return false

        pendingBet = false

        const up = useUltraPlayStore.getState()
        const bet = getBet()
        const win = Math.max(0, balance - betStartBalance + bet)

        up.addSpin({
            spinNumber: up.currentSpinNumber + 1,
            bet,
            win,
            winMultiplier: bet > 0 ? win / bet : 0,
            balance,
            type: 'NORMAL',
        })
        up.decrementRemainingSpins()

        return true
    }

    // Ends the feature view in Ultra Play: collapse the "+N" marker and clear feature state. Called
    // either from FEATURE_STATUS_CHANGED 'completed' or — earlier and more reliably — from the first
    // base autoplay count after the feature (free spins emit no autoplay counts, so a count means the
    // base game has resumed; the 'completed' message is tied to the outro animation and lags when the
    // outro is skipped). Returns true if it actually cleared an active feature.
    const completeFeature = (): boolean => {
        const up = useUltraPlayStore.getState()

        if (!up.isInFeature) return false

        if (featureParentSpin !== null) {
            up.setSpinExpanded(featureParentSpin, false)
        }

        up.setIsInFeature(false)
        featureParentSpin = null
        featureCount = 0
        lastFreespinIndex = 0
        pendingBet = false

        return true
    }

    return {
        // Drop any pending bet when a session ends (→ READY) so the next session starts clean.
        resetPending: () => {
            pendingBet = false
        },

        // Unified feature lifecycle for Ultra Play (started / completed).
        onFeatureStatusChanged: (payload: FeatureStatusChangedMessage['payload']) => {
            const up = useUltraPlayStore.getState()

            // Only relevant while an Ultra Play session is running
            if (up.status !== 'PLAYING' && up.status !== 'PAUSED') return

            if (payload.status === 'started') {
                const isAutoplayable = !!payload.isAutoplayable

                // The triggering bet has resolved — record it (one count late, by design) so it's the
                // most recent row, then promote that row to the feature marker.
                finalizePendingBet(usePlayerInfoStore.getState().balance)
                pendingBet = false

                up.setIsInFeature(true)
                lastFreespinIndex = 0
                featureCount = 0
                featureLastBalance = usePlayerInfoStore.getState().balance

                // The spin that triggered the feature is the most recent row — promote it to the group
                // "+N" marker (it keeps its own win/balance) and expand it. Free spins are recorded as
                // its sub-rows. Fallback to a fresh marker if there is no prior spin.
                // Read fresh — finalizePendingBet() above may have just added the triggering row.
                const triggerSpin = useUltraPlayStore.getState().spins[0]

                if (triggerSpin) {
                    up.updateSpin(triggerSpin.spinNumber, { type: 'FREE_SPIN', isExpanded: true })
                    featureParentSpin = triggerSpin.spinNumber
                } else {
                    featureParentSpin = null
                }

                const shouldAutoplayThrough = !up.stopOnFeature && isAutoplayable

                if (!shouldAutoplayThrough) {
                    // Cases 1.1 / 1.2 / 2.2 — pause and present the choice dialog.
                    up.setFeatureDialogAutoplayable(isAutoplayable)
                    up.pause()
                    up.setShowFeatureChoiceDialog(true)
                }
                // else case 2.1 — play through silently; spins are grouped on count updates
            } else if (payload.status === 'completed') {
                // Fallback path — if the base game hasn't resumed yet (so the count handler didn't
                // already finish the feature), clear it here and skip the upcoming resume-artifact count.
                if (completeFeature()) {
                    skipNextSpinCount = true
                }
            }
            // 'updated' — ignored for now
        },

        onFreespinCount: (data: Pick<InfoLineUpdateFreespinCountMessage, 'currentFreespin' | 'totalFreespins'>) => {
            const up = useUltraPlayStore.getState()

            if (up.status !== 'PLAYING' && up.status !== 'PAUSED') return

            if (!up.isInFeature) return

            // A retrigger raises totalFreespins mid-feature (e.g. 15 → 25). The increase belongs to
            // the free spin currently being played; show "+delta" on its row.
            const prevTotal = featureCount
            const retriggerDelta =
                prevTotal > 0 && data.totalFreespins > prevTotal ? data.totalFreespins - prevTotal : 0

            featureCount = data.totalFreespins

            // Keep the "+N" marker total in sync (initial award + any retriggers).
            if (featureParentSpin !== null) {
                up.updateSpin(featureParentSpin, { freeSpinsAwarded: data.totalFreespins })
            }

            // updateFreespinCount fires multiple times per index — only record on advance.
            if (data.currentFreespin <= lastFreespinIndex) return

            lastFreespinIndex = data.currentFreespin

            const balance = usePlayerInfoStore.getState().balance
            // This game does not emit per-spin win during free spins — derive it from the balance
            // delta since the previous recorded free spin.
            const win = Math.max(0, balance - featureLastBalance)

            featureLastBalance = balance

            if (featureParentSpin === null) {
                // No triggering spin to promote (edge case) — create a standalone marker.
                const spinNumber = up.currentSpinNumber + 1

                up.addSpin(
                    {
                        spinNumber,
                        bet: 0,
                        win: 0,
                        winMultiplier: 0,
                        balance,
                        type: 'FREE_SPIN',
                        freeSpinsAwarded: data.totalFreespins || undefined,
                    },
                    true
                )
                featureParentSpin = spinNumber
            }

            up.addSubSpin(featureParentSpin, {
                win,
                winMultiplier: 0,
                balance,
                type: 'FREE_SPIN',
                freeSpinsAwarded: retriggerDelta || undefined,
            })
        },

        onAutoplayCount: (payload: number | undefined) => {
            // Track the engine's autoplay counter so AUTOPLAY_STOPPED can tell a finished batch
            // (payload reached 0) from a mid-batch feature pause (payload still > 0).
            lastEnginePayload = Number(payload ?? -1)

            // Skip the count-update message emitted by resumeAutoplay() after a feature ends
            if (skipNextSpinCount) {
                skipNextSpinCount = false

                return
            }

            const up = useUltraPlayStore.getState()

            if (up.status === 'READY') {
                pendingBet = false

                return
            }

            // Free spins are recorded from INFO_LINE_UPDATE_FREESPIN_COUNT and emit no autoplay counts
            // — so a count arriving here means the base game has resumed. Finish the feature now (the
            // 'completed' message lags, being tied to the skipped outro animation) and skip this
            // resume-artifact count; real base spins follow.
            if (up.isInFeature) {
                if (up.status === 'PLAYING') completeFeature()

                return
            }

            const { balance } = usePlayerInfoStore.getState()

            // This count starts a new bet, which means the previous bet has resolved — its result is
            // now in `balance`, so record it (one count late, by design — no phantom first row, no
            // off-by-one balance).
            finalizePendingBet(balance)

            // Target reached — stop the engine before it overruns; don't start another bet.
            if (useUltraPlayStore.getState().remainingSpins === 0) {
                pendingBet = false
                send({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED })
                useUltraPlayStore.getState().stop()

                return
            }

            // Mark the just-started bet as pending; its result lands by the next count (or on stop).
            betStartBalance = balance
            pendingBet = true
        },

        onAutoplayStopped: () => {
            const up = useUltraPlayStore.getState()

            // PAUSED: user paused via UI. READY: already cleaned up. Only act while PLAYING.
            if (up.status !== 'PLAYING') return

            // A feature already started — the engine paused for it and will resume itself.
            if (up.isInFeature) return

            const stoppedBalance = usePlayerInfoStore.getState().balance

            // The last started bet has resolved — record it now (its row is committed here, not on a
            // following count, since the engine has stopped).
            finalizePendingBet(stoppedBalance)

            // Re-read: remaining spins changed when the pending bet was recorded.
            const upAfter = useUltraPlayStore.getState()

            // Out of funds — the balance can no longer cover the selected bet. Pause and prompt the
            // player to top up instead of looping.
            if (upAfter.remainingSpins !== 0 && getBet() > 0 && stoppedBalance < getBet()) {
                upAfter.pause()
                upAfter.setShowInsufficientBalance(true)

                return
            }

            // Re-issue autoplay in batches so the requested Ultra Play count is honoured regardless of
            // the engine's per-batch autoplay cap (autoplayLimit).
            const retriggerAutoplay = (remaining: number) => {
                const engineOptions = useConfigurationStore.getState().autoplayOptions
                const index = engineOptions.reduce((best: number, count: number, i: number) => {
                    if (count === -1) return best

                    const bestCount = engineOptions[best] === -1 ? Infinity : engineOptions[best]

                    return Math.abs(count - remaining) < Math.abs(bestCount - remaining) ? i : best
                }, 0)

                send({ type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED, payload: index })

                const sbStore = useSpinButtonSectionStore.getState()

                sbStore.updateAutoplayMode(true)
                sbStore.updateAutoplayCount(remaining - 1)
                sbStore.updateSelectedAutoplayIndex(index)
            }

            if (upAfter.pendingAutoplayRestart) {
                // Spins count changed mid-session — restart immediately with the new count.
                upAfter.setPendingAutoplayRestart(false)
                retriggerAutoplay(upAfter.remainingSpins)

                return
            }

            if (upAfter.remainingSpins <= 0) {
                // Natural completion — stop but preserve spins for history view.
                upAfter.stop()

                return
            }

            // Spins still remain. Use the engine's last autoplay counter to disambiguate:
            //  - payload === 0  → the batch finished at its cap → continue with the next batch
            //  - payload  >  0  → the engine paused mid-batch for a feature (FEATURE_STATUS_CHANGED
            //                     'started' arrives a tick later) → keep the session alive; it resumes itself
            if (lastEnginePayload === 0) {
                retriggerAutoplay(upAfter.remainingSpins)
            }
        },
    }
}

export type UltraPlaySpinRecorder = ReturnType<typeof createUltraPlaySpinRecorder>
